import axios from "axios";
import { disableStaffPush } from '@/lib/staffPwa';
import { beginRequest } from '@/lib/requestActivity';
import { API_BASE } from "@/lib/api";
import { invalidate as cacheInvalidate } from "@/lib/queryCache";

const TOKEN_KEY = "pout.staff.token";
const USER_KEY = "pout.staff.user";
const EXP_KEY = "pout.staff.exp";

export function getToken() {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
export function getStoredUser() {
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}
export function getStoredExpiry() {
    try { return localStorage.getItem(EXP_KEY) || null; } catch { return null; }
}
export function saveSession({ token, user, expires_at }) {
    try {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        if (expires_at) localStorage.setItem(EXP_KEY, expires_at);
    } catch {}
}
export function clearSession() {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(EXP_KEY);
    } catch {}
}
export function isSessionExpired() {
    const exp = getStoredExpiry();
    if (!exp) return false;
    return new Date(exp).getTime() <= Date.now();
}

const client = axios.create({
    baseURL: API_BASE,
    timeout: 30000,
    headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    if (!cfg.background) cfg.finishActivity = beginRequest();
    return cfg;
});

client.interceptors.response.use(
    (r) => { r.config?.finishActivity?.(); return r; },
    (err) => {
        err.config?.finishActivity?.();
        if (err?.response?.status === 401) {
            clearSession();
            if (!window.location.pathname.startsWith("/staff/login")) {
                window.location.href = "/staff/login";
            }
        }
        return Promise.reject(err);
    }
);

function unwrap(err) {
    const status = err?.response?.status;
    const raw = err?.response?.data;
    // Cloudflare edge / gateway errors — never surface those raw. They arrive
    // either as HTML (Accept: text/html) or as a JSON envelope
    //   { cloudflare_error: true, detail: "...", error_category: "origin", ... }
    // whenever Accept: application/json is set (as axios does).
    const isCloudflareHtml = typeof raw === "string" && (raw.trim().startsWith("<") || raw.includes("Cloudflare"));
    const isCloudflareJson = raw && typeof raw === "object" && (raw.cloudflare_error === true || raw.error_category === "origin" || (typeof raw.title === "string" && raw.title.toLowerCase().includes("bad gateway")));
    if (isCloudflareHtml || isCloudflareJson) {
        const friendly = status === 502 || status === 504
            ? "Amelia is not responding right now. Please try again shortly."
            : "The server is temporarily unavailable. Please try again shortly.";
        const e = new Error(friendly);
        e.status = status;
        return e;
    }
    const detail = raw?.detail;
    const msg = typeof detail === "string" ? detail : (detail?.[0]?.msg || err.message);
    const e = new Error(msg || "Something went wrong.");
    e.status = status;
    return e;
}

export const staffApi = {
    async staffUpdates() {
        try {return (await client.get('/staff/updates', {background:true})).data;} catch(e){throw unwrap(e);}
    },
    async readStaffUpdates(ids) {
        try {return (await client.post('/staff/updates/read', {ids}, {background:true})).data;} catch(e){throw unwrap(e);}
    },
    async duplicateCustomers() {
        try {return (await client.get('/staff/customer-merges', {timeout:120000})).data;} catch(e){throw unwrap(e);}
    },
    async previewCustomerMerge(keep_id, other_id) {
        try {return (await client.post('/staff/customer-merges/preview', {keep_id,other_id}, {timeout:120000})).data;} catch(e){throw unwrap(e);}
    },
    async mergeCustomers(value) {
        try {return (await client.post('/staff/customer-merges', value, {timeout:120000})).data;} catch(e){throw unwrap(e);}
    },
    async reviews(page=0) {
        try {return (await client.get('/staff/reviews',{params:{page}})).data;} catch(e){throw unwrap(e);}
    },
    async googleReviews() {
        try {return (await client.get('/staff/reviews/google',{background:true})).data;} catch(e){throw unwrap(e);}
    },
    async matchGoogleReviews() {
        try {return (await client.post('/staff/reviews/match-google',{}, {timeout:120000})).data;} catch(e){throw unwrap(e);}
    },
    async saveReviewSettings(value) {
        try {return (await client.put('/staff/reviews/settings',value)).data;} catch(e){throw unwrap(e);}
    },
    async customerReview(id) {
        try {return (await client.get(`/staff/reviews/customers/${id}`,{background:true})).data;} catch(e){throw unwrap(e);}
    },
    async recordCustomerReview(id,choice,google_confirmation=false) {
        try {return (await client.post(`/staff/reviews/customers/${id}`,{choice,google_confirmation})).data;} catch(e){throw unwrap(e);}
    },
    async cancellationPreview(id) {
        try { return (await client.get(`/staff/appointments/${id}/cancellation`)).data; } catch(e) { throw unwrap(e); }
    },
    async cancelUnderPolicy(id,payload) {
        try { const r=(await client.post(`/staff/appointments/${id}/cancellation`,payload,{timeout:120000})).data; cacheInvalidate('staff:'); return r; } catch(e) { throw unwrap(e); }
    },
    async cancellationPolicy() {
        try { return (await client.get('/staff/settings/cancellation-policy')).data; } catch(e) { throw unwrap(e); }
    },
    async saveCancellationPolicy(payload) {
        try { return (await client.put('/staff/settings/cancellation-policy',payload)).data; } catch(e) { throw unwrap(e); }
    },
    async cancellations(page=0) {
        try { return (await client.get('/staff/cancellations',{params:{page}})).data; } catch(e) { throw unwrap(e); }
    },
    async resumeCancellation(id) {
        try { const r=(await client.post(`/staff/cancellations/${id}/resume`,{},{timeout:120000})).data; cacheInvalidate('staff:'); return r; } catch(e) { throw unwrap(e); }
    },
    async reviewCancellationRefund(id,payload) {
        try { const r=(await client.post(`/staff/cancellations/${id}/refund`,payload,{timeout:120000})).data; cacheInvalidate('staff:'); return r; } catch(e) { throw unwrap(e); }
    },
    async serviceUpdate(action = "", payload = {}) {
        try {
            const url = `/staff/communications/service-update${action ? "/" + action : ""}`;
            return (await (action ? client.post(url, payload, {timeout:120000}) : client.get(url))).data;
        } catch (e) { throw unwrap(e); }
    },
    async getVisit(id) {
        try { return (await client.get(`/staff/appointments/${id}/visit`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async sendVisitDeposit(id, payload) {
        try { return (await client.post(`/staff/appointments/${id}/visit/deposit`, payload, {timeout:120000})).data; }
        catch (e) { throw unwrap(e); }
    },
    async saveVisit(id, payload) {
        try {
            const result = (await client.post(`/staff/appointments/${id}/visit`, payload, {timeout:120000})).data;
            ['staff:appointments','staff:dashboard','staff:calendar','staff:payments','staff:appointment:','staff:timeline','staff:customers'].forEach(cacheInvalidate);
            return result;
        } catch (e) { throw unwrap(e); }
    },
    async catalogue() {
        try { return (await client.get("/staff/catalogue")).data; } catch (e) { throw unwrap(e); }
    },
    async saveCatalogue(payload) {
        try { return (await client.put("/staff/catalogue", payload)).data; } catch (e) { throw unwrap(e); }
    },
    async login(email, password) {
        try { return (await client.post("/staff/auth/login", { email, password })).data; }
        catch (e) { throw unwrap(e); }
    },
    async me() {
        try { return (await client.get("/staff/auth/me")).data; }
        catch (e) { throw unwrap(e); }
    },
    async logout() {
        await disableStaffPush();
        try { await client.post("/staff/auth/logout"); } catch {}
        clearSession();
    },
    async dashboard(refresh = false) {
        try { return (await client.get("/staff/dashboard", {params: refresh ? {refresh:true} : {}})).data; }
        catch (e) { throw unwrap(e); }
    },
    async listAppointments(params = {}) {
        try { return (await client.get("/staff/appointments", { params })).data; }
        catch (e) { throw unwrap(e); }
    },
    async getAppointment(id) {
        try { return (await client.get(`/staff/appointments/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async changeStatus(id, status) {
        try {
            const r = (await client.post(`/staff/appointments/${id}/status`, { status })).data;
            cacheInvalidate("staff:customers");
            cacheInvalidate("staff:appointments");
            cacheInvalidate("staff:dashboard");
            cacheInvalidate("staff:calendar");
            cacheInvalidate("staff:payments");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async changeTreatment(id, payload) {
        try { const r=(await client.post(`/staff/appointments/${id}/treatment`,payload)).data; cacheInvalidate('staff:'); return r; }
        catch(e) { throw unwrap(e); }
    },
    async deleteBooking(id, payload) {
        try { const r=(await client.post(`/staff/appointments/${id}/delete`,payload)).data; cacheInvalidate('staff:'); return r; }
        catch(e) { throw unwrap(e); }
    },
    async reschedule(id, date, time) {
        try {
            const r = (await client.post(`/staff/appointments/${id}/reschedule`, { date, time })).data;
            cacheInvalidate("staff:appointments");
            cacheInvalidate("staff:dashboard");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async updateNotes(id, internalNotes) {
        try {
            const r = (await client.post(`/staff/appointments/${id}/notes`, { internalNotes })).data;
            cacheInvalidate(`staff:appointment:${id}`);
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async listCustomers(search = "", limit = 50, page = 1) {
        try {
            return (await client.get("/staff/customers", { params: { search, limit, page } })).data;
        } catch (e) { throw unwrap(e); }
    },
    async getCustomer(id) {
        try { return (await client.get(`/staff/customers/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async customerAppointments(id) {
        try { return (await client.get(`/staff/customers/${id}/appointments`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async createCustomer(payload) {
        try {
            const r = (await client.post("/staff/customers", payload)).data;
            cacheInvalidate("staff:customers");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async updateCustomer(id, payload) {
        try {
            const r = (await client.put(`/staff/customers/${id}`, payload)).data;
            cacheInvalidate("staff:customers");
            cacheInvalidate(`staff:customer:${id}`);
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async deleteCustomer(id) {
        try {
            const r = (await client.delete(`/staff/customers/${id}`)).data;
            cacheInvalidate("staff:customers");
            cacheInvalidate(`staff:customer:${id}`);
            return r;
        } catch (e) { throw unwrap(e); }
    },
    // ---- Communication defaults + bulk actions (Sprint 34) ----
    async getCommunicationDefaults() {
        try { return (await client.get("/staff/communication-defaults")).data; }
        catch (e) { throw unwrap(e); }
    },
    async setCommunicationDefaults(defaults) {
        try { return (await client.put("/staff/communication-defaults", defaults)).data; }
        catch (e) { throw unwrap(e); }
    },
    async applyServiceCommsDefaults() {
        try { return (await client.post("/staff/communication-defaults/apply-service-comms")).data; }
        catch (e) { throw unwrap(e); }
    },
    async applyMarketingSoftOptIn() {
        try { return (await client.post("/staff/communication-defaults/apply-marketing-soft-opt-in")).data; }
        catch (e) { throw unwrap(e); }
    },
    // ---- Marketing (Sprint 35) ----
    async testSendComms(payload) {
        try { return (await client.post("/staff/comms/test-send", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async marketingLibrary() {
        try { return (await client.get("/staff/marketing/library")).data; } catch(e) { throw unwrap(e); }
    },
    async createWhatsAppDraft(payload) {
        try { return (await client.post("/staff/marketing/whatsapp-drafts",payload)).data; } catch(e) { throw unwrap(e); }
    },
    async submitWhatsAppDraft(id) {
        try { return (await client.post(`/staff/marketing/whatsapp-drafts/${id}/submit`)).data; } catch(e) { throw unwrap(e); }
    },
    async listMarketingTemplates() {
        try { return (await client.get("/staff/marketing/templates")).data; }
        catch (e) { throw unwrap(e); }
    },
    async createMarketingTemplate(payload) {
        try { return (await client.post("/staff/marketing/templates", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async patchMarketingTemplate(id, payload) {
        try { return (await client.patch(`/staff/marketing/templates/${id}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async deleteMarketingTemplate(id) {
        try { return (await client.delete(`/staff/marketing/templates/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async listMarketingCampaigns() {
        try { return (await client.get("/staff/marketing/campaigns")).data; }
        catch (e) { throw unwrap(e); }
    },
    async createMarketingCampaign(payload) {
        try { return (await client.post("/staff/marketing/campaigns", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async patchMarketingCampaign(id, payload) {
        try { return (await client.patch(`/staff/marketing/campaigns/${id}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async deleteMarketingCampaign(id) {
        try { return (await client.delete(`/staff/marketing/campaigns/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async duplicateMarketingCampaign(id) {
        try { return (await client.post(`/staff/marketing/campaigns/${id}/duplicate`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async previewCampaignRecipients(id) {
        try { return (await client.post(`/staff/marketing/campaigns/${id}/preview-recipients`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async sendMarketingCampaign(id) {
        try { return (await client.post(`/staff/marketing/campaigns/${id}/send`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async scheduleMarketingCampaign(id, scheduledAt) {
        try { return (await client.post(`/staff/marketing/campaigns/${id}/schedule`, { scheduled_at: scheduledAt })).data; }
        catch (e) { throw unwrap(e); }
    },
    async unscheduleMarketingCampaign(id) {
        try { return (await client.post(`/staff/marketing/campaigns/${id}/unschedule`)).data; }
        catch (e) { throw unwrap(e); }
    },
    // ---- WhatsApp Business (Feb 2026) ----
    async whatsappStatus() {
        try { return (await client.get("/staff/whatsapp/status")).data; }
        catch (e) { throw unwrap(e); }
    },
    async whatsappAutomationStatus() {
        try { return (await client.get("/staff/whatsapp/automation-status")).data; }
        catch (e) { throw unwrap(e); }
    },
    async whatsappTemplates() {
        try { return (await client.get("/staff/whatsapp/templates")).data; }
        catch (e) { throw unwrap(e); }
    },
    async whatsappWamidLookup(wamid) {
        try { return (await client.get(`/staff/whatsapp/wamid/${encodeURIComponent(wamid)}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async whatsappDiagnose() {
        try { return (await client.get("/staff/whatsapp/diagnose")).data; }
        catch (e) { throw unwrap(e); }
    },
    async getCustomerEvents(customerId) {
        try { return (await client.get(`/staff/customers/${customerId}/events`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async disableAllMarketing() {
        try { return (await client.post("/staff/communication-defaults/disable-all-marketing")).data; }
        catch (e) { throw unwrap(e); }
    },
    async services() {
        try { return (await client.get("/staff/services")).data; }
        catch (e) { throw unwrap(e); }
    },
    async getService(id) {
        try { return (await client.get(`/staff/services/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async menuCreate(kind, payload) {
        try { const r = await client.post(`/staff/service-menu/${kind}`,payload); cacheInvalidate('staff:'); return r.data; }
        catch(e) { const error=unwrap(e);error.creationRejected=[404,422].includes(e.response?.status);throw error; }
    },
    async menuDelete(kind, id) {
        try { const r = await client.delete(`/staff/service-menu/${kind}/${id}`); cacheInvalidate('staff:'); return r.data; }
        catch(e) { throw unwrap(e); }
    },
    async menuMove(id, category_id) {
        try { const r = await client.put(`/staff/service-menu/services/${id}/category`,{category_id}); cacheInvalidate('staff:'); return r.data; }
        catch(e) { throw unwrap(e); }
    },
    async setCategoryColour(id, colour) {
        const result = (await client.put(`/staff/service-menu/categories/${id}/colour`, {colour})).data;
        cacheInvalidate('staff:');
        return result;
    },
    async orderCategories(ids, revision) {
        try { const r = await client.put('/staff/service-menu/categories/order',{ids,revision}); cacheInvalidate('staff:'); return r.data; }
        catch(e) { throw unwrap(e); }
    },
    async createVariant(id, payload) {
        try { return (await client.post(`/staff/catalogue/${id}/variants`, payload)).data; }
        catch (e) {
            const error = unwrap(e);
            // These responses occur before the external creation write. An
            // ambiguous response must retain its key to prevent duplicates.
            error.creationRejected = [404, 422].includes(e.response?.status);
            throw error;
        }
    },
    async updateService(id, patch) {
        try {
            const r = (await client.put(`/staff/services/${id}`, patch)).data;
            cacheInvalidate("staff:services");
            cacheInvalidate(`staff:service:${id}`);
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async employees() {
        try { return (await client.get("/staff/employees")).data; }
        catch (e) { throw unwrap(e); }
    },
    async availability() {
        try { return (await client.get("/staff/availability")).data; }
        catch (e) { throw unwrap(e); }
    },

    // ---- Availability write API (Amelia) ----
    async calendarBlock(employeeId, payload) {
        try {
            const r = (await client.post(`/staff/availability/employees/${employeeId}/calendar-block`, payload)).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availServiceDays(employeeId) {
        try { return (await client.get(`/staff/availability/employees/${employeeId}/service-days`)).data; }
        catch(e) { throw unwrap(e); }
    },
    async availSaveServiceDays(employeeId, payload) {
        try {
            const result = (await client.put(`/staff/availability/employees/${employeeId}/service-days`,payload)).data;
            cacheInvalidate('staff:'); return result;
        } catch(e) { throw unwrap(e); }
    },
    async availGetEmployee(employeeId) {
        try { return (await client.get(`/staff/availability/employees/${employeeId}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async availConflicts(employeeId, startDate, endDate) {
        try { return (await client.get(`/staff/availability/conflicts`, { params: { employee_id: employeeId, start_date: startDate, end_date: endDate } })).data; }
        catch (e) { throw unwrap(e); }
    },
    async availAddDayOff(employeeId, payload, force = false) {
        try {
            const r = (await client.post(`/staff/availability/employees/${employeeId}/days-off`, payload, { params: { force } })).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            cacheInvalidate("staff:dashboard");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availUpdateDayOff(employeeId, dayOffId, payload, force = false) {
        try {
            const r = (await client.put(`/staff/availability/employees/${employeeId}/days-off/${dayOffId}`, payload, { params: { force } })).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availDeleteDayOff(employeeId, dayOffId) {
        try {
            const r = (await client.delete(`/staff/availability/employees/${employeeId}/days-off/${dayOffId}`)).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availAddSpecialDay(employeeId, payload, force = false) {
        try {
            const r = (await client.post(`/staff/availability/employees/${employeeId}/special-days`, payload, { params: { force } })).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availUpdateSpecialDay(employeeId, specialId, payload, force = false) {
        try {
            const r = (await client.put(`/staff/availability/employees/${employeeId}/special-days/${specialId}`, payload, { params: { force } })).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availDeleteSpecialDay(employeeId, specialId) {
        try {
            const r = (await client.delete(`/staff/availability/employees/${employeeId}/special-days/${specialId}`)).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async availUpsertWeekDay(employeeId, payload) {
        try {
            const r = (await client.put(`/staff/availability/employees/${employeeId}/week-day`, payload)).data;
            cacheInvalidate("staff:availability");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async payments() {
        try { return (await client.get("/staff/payments")).data; }
        catch (e) { throw unwrap(e); }
    },
    async reports(startDate, endDate) {
        try { return (await client.get("/staff/reports", { params: { start_date: startDate, end_date: endDate } })).data; }
        catch (e) { throw unwrap(e); }
    },
    async bookingSlots(serviceIds, date) {
        try { return (await client.get('/staff/booking-slots', { params: { service_ids: serviceIds.join(','), date } })).data; }
        catch (e) { throw unwrap(e); }
    },
    async manualBooking(payload) {
        try {
            const r = (await client.post(payload.service_ids ? "/staff/manual-visit" : "/staff/manual-booking", payload, { timeout: 120000 })).data;
            cacheInvalidate("staff:appointments");
            cacheInvalidate("staff:dashboard");
            cacheInvalidate("staff:calendar");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async recordManualPayment(id, payload) {
        try {
            const result = (await client.post(`/staff/appointments/${id}/payment/manual`, payload)).data;
            cacheInvalidate("staff:");
            return result;
        } catch (e) { throw unwrap(e); }
    },

    // ---------------- Forms (owner-side) ----------------
    async listForms(params = {}) {
        try { return (await client.get("/staff/forms", { params })).data; }
        catch (e) { throw unwrap(e); }
    },

    // ---------------- Form Categories (Sprint 33) ----------------
    async listFormCategories(includeArchived = false) {
        try { return (await client.get("/staff/form-categories", { params: { include_archived: includeArchived } })).data; }
        catch (e) { throw unwrap(e); }
    },
    async createFormCategory(payload) {
        try { return (await client.post("/staff/form-categories", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async patchFormCategory(id, payload) {
        try { return (await client.patch(`/staff/form-categories/${id}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async deleteFormCategory(id, target_id) {
        try { return (await client.delete(`/staff/form-categories/${id}`,{params:{target_id:target_id || undefined}})).data; }
        catch (e) { throw unwrap(e); }
    },
    async reorderFormCategories(ordered_ids) {
        try { return (await client.post("/staff/form-categories/reorder", { ordered_ids })).data; }
        catch (e) { throw unwrap(e); }
    },
    async listCategoryServiceMappings(catId) {
        try { return (await client.get(`/staff/form-categories/${catId}/service-mappings`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async addCategoryServiceMapping(catId, payload) {
        try { return (await client.post(`/staff/form-categories/${catId}/service-mappings`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async removeCategoryServiceMapping(catId, mappingId) {
        try { return (await client.delete(`/staff/form-categories/${catId}/service-mappings/${mappingId}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async listUnmappedServices() {
        try { return (await client.get("/staff/service-category-mappings/unmapped")).data; }
        catch (e) { throw unwrap(e); }
    },
    async listAllServiceMappings() {
        try { return (await client.get("/staff/service-category-mappings")).data; }
        catch (e) { throw unwrap(e); }
    },
    async suggestServiceMappings() {
        try { return (await client.post("/staff/service-category-mappings/suggest")).data; }
        catch (e) { throw unwrap(e); }
    },
    async bulkApplyServiceMappings(items) {
        try { return (await client.post("/staff/service-category-mappings/bulk-apply", { items })).data; }
        catch (e) { throw unwrap(e); }
    },

    async createForm(payload) {
        try { return (await client.post("/staff/forms", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async getForm(id) {
        try { return (await client.get(`/staff/forms/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async patchForm(id, payload) {
        try { return (await client.patch(`/staff/forms/${id}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async formStatus(id, status) {
        try { return (await client.post(`/staff/forms/${id}/status`, { status })).data; }
        catch (e) { throw unwrap(e); }
    },
    async duplicateForm(id) {
        try { return (await client.post(`/staff/forms/${id}/duplicate`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async saveFormDraft(id, sections) {
        try { return (await client.put(`/staff/forms/${id}/draft`, { sections })).data; }
        catch (e) { throw unwrap(e); }
    },
    async publishForm(id) {
        try { return (await client.post(`/staff/forms/${id}/publish`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async archiveForm(id) {
        try { return (await client.post(`/staff/forms/${id}/archive`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async restoreForm(id) {
        try { return (await client.post(`/staff/forms/${id}/restore`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async deleteForm(id) {
        try { return (await client.delete(`/staff/forms/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async saveFormAsNew(id, payload = {}) {
        try { return (await client.post(`/staff/forms/${id}/save-as-new`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async seedStandardForms(force = false) {
        try { return (await client.post(`/staff/forms/seed-standard-templates`, null, { params: { force } })).data; }
        catch (e) { throw unwrap(e); }
    },

    // ---------------- Service form-assignments ----------------
    async listServiceForms(serviceId) {
        try { return (await client.get(`/staff/services/${serviceId}/form-assignments`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async assignFormToService(serviceId, payload) {
        try { return (await client.post(`/staff/services/${serviceId}/form-assignments`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async patchServiceFormAssignment(serviceId, assignmentId, payload) {
        try { return (await client.patch(`/staff/services/${serviceId}/form-assignments/${assignmentId}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async removeServiceFormAssignment(serviceId, assignmentId) {
        try { return (await client.delete(`/staff/services/${serviceId}/form-assignments/${assignmentId}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async reorderServiceFormAssignments(serviceId, orderedIds) {
        try { return (await client.post(`/staff/services/${serviceId}/form-assignments/reorder`, { ordered_ids: orderedIds })).data; }
        catch (e) { throw unwrap(e); }
    },

    // ---------------- Stage 2 — packages, submissions ----------------
    async preparePackage(appointmentId, payload) {
        try { return (await client.post(`/staff/appointments/${appointmentId}/form-package`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async getPackage(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/form-package`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async packageAction(packageId, action) {
        try { return (await client.post(`/staff/form-packages/${packageId}/action`, { action })).data; }
        catch (e) { throw unwrap(e); }
    },
    async listFormReviews(offset = 0) {
        try { return (await client.get('/staff/form-reviews', {params: {offset}})).data; }
        catch (e) { throw unwrap(e); }
    },
    async reviewSubmission(id) {
        try { return (await client.post(`/staff/submissions/${id}/review`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async signPractitioner(id, field_id, signature) {
        try { return (await client.post(`/staff/submissions/${id}/practitioner-signature`, {field_id, signature})).data; }
        catch (e) { throw unwrap(e); }
    },
    async pendingMessages(offset = 0) {
        try { return (await client.get('/staff/communications/pending-messages', {params: {offset}})).data; }
        catch (e) { throw unwrap(e); }
    },
    async listAppointmentSubmissions(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/submissions`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async listCustomerSubmissions(customerId) {
        try { return (await client.get(`/staff/customers/${customerId}/submissions`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async getSubmission(submissionId) {
        try { return (await client.get(`/staff/submissions/${submissionId}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async getSignature(signatureId) {
        try { return (await client.get(`/staff/signatures/${signatureId}`)).data; }
        catch (e) { throw unwrap(e); }
    },

    // ---------------- Stage 3 — Communications ----------------
    async commsMeta() {
        try { return (await client.get("/staff/communications/meta")).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsListTemplates(params = {}) {
        try { return (await client.get("/staff/communications/templates", { params })).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsCreateTemplate(payload) {
        try { return (await client.post("/staff/communications/templates", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsGetTemplate(id) {
        try { return (await client.get(`/staff/communications/templates/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsPatchTemplate(id, payload) {
        try { return (await client.patch(`/staff/communications/templates/${id}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsTemplateStatus(id, status) {
        try { return (await client.post(`/staff/communications/templates/${id}/status`, { status })).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsDuplicateTemplate(id) {
        try { return (await client.post(`/staff/communications/templates/${id}/duplicate`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsDeleteTemplate(id) {
        try { return (await client.delete(`/staff/communications/templates/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsPreview(payload) {
        try { return (await client.post("/staff/communications/templates/preview", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsListRules(params = {}) {
        try { return (await client.get("/staff/communications/rules", { params })).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsCreateRule(payload) {
        try { return (await client.post("/staff/communications/rules", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsPatchRule(id, payload) {
        try { return (await client.patch(`/staff/communications/rules/${id}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsDeleteRule(id) {
        try { return (await client.delete(`/staff/communications/rules/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsListQueue(params = {}) {
        try { return (await client.get("/staff/communications/queue", { params })).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsCreateQueueItem(payload) {
        try { return (await client.post("/staff/communications/queue", payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsQueueStatus(id, status, note) {
        try { return (await client.post(`/staff/communications/queue/${id}/status`, { status, note })).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsDeleteQueueItem(id) {
        try { return (await client.delete(`/staff/communications/queue/${id}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsGetPreferences(customerId) {
        try { return (await client.get(`/staff/communications/preferences/${customerId}`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsUpsertPreferences(customerId, payload) {
        try { return (await client.put(`/staff/communications/preferences/${customerId}`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsAppointmentTimeline(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/communications`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async commsCustomerTimeline(customerId) {
        try { return (await client.get(`/staff/customers/${customerId}/communications`)).data; }
        catch (e) { throw unwrap(e); }
    },

    // ---------------- Amelia Stripe payment links ----------------
    async getAppointmentPayment(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/payment`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async generatePaymentLink(appointmentId, { regenerate = false, redirect_url, deposit_target, purpose = "deposit" } = {}) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/payment/link`, { regenerate, redirect_url, deposit_target, purpose })).data;
            cacheInvalidate("staff:payments");
            cacheInvalidate("staff:dashboard");
            cacheInvalidate(`staff:appointment:${appointmentId}`);
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async syncAppointmentPayment(appointmentId) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/payment/sync`)).data;
            cacheInvalidate("staff:payments");
            cacheInvalidate("staff:dashboard");
            return r;
        } catch (e) { throw unwrap(e); }
    },

    // ---- Checkout, Timeline, Automation Settings (Iter 24) ----
    async getCheckout(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/checkout`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async saveCheckout(appointmentId, payload) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/checkout`, payload)).data;
            cacheInvalidate("staff:appointments");
            cacheInvalidate("staff:dashboard");
            cacheInvalidate("staff:calendar");
            cacheInvalidate("staff:payments");
            cacheInvalidate(`staff:appointment:${appointmentId}`);
            cacheInvalidate("staff:timeline");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async customerTimeline(customerId) {
        try { return (await client.get(`/staff/customers/${customerId}/timeline`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async getAutomationSettings() {
        try { return (await client.get(`/staff/automation-settings`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async updateAutomationSettings(payload) {
        try { return (await client.put(`/staff/automation-settings`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async smtpStatus() {
        try { return (await client.get(`/staff/communications/smtp/status`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async getReadyEarly(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/ready-early`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async sendReadyEarly(appointmentId, payload, force = false) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/ready-early`, payload, { params: { force } })).data;
            cacheInvalidate(`staff:appointment:${appointmentId}`);
            cacheInvalidate("staff:timeline");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    // --- Sprint 27: manual Medical Form + readiness ---
    async appointmentReadiness(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/readiness`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async sendMedicalForm(appointmentId, payload) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/send-medical-form`, payload)).data;
            cacheInvalidate(`staff:appointment:${appointmentId}`);
            cacheInvalidate("staff:timeline");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async markMedicalReviewed(appointmentId, dispatchId) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/medical-form/mark-reviewed`, { dispatch_id: dispatchId })).data;
            cacheInvalidate(`staff:appointment:${appointmentId}`);
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async listForms() {
        try { return (await client.get(`/staff/forms`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async seedV2Forms() {
        try {
            const r = (await client.post(`/staff/forms/seed-v2-templates`)).data;
            cacheInvalidate("staff:forms");
            return r;
        } catch (e) { throw unwrap(e); }
    },
    async flushQueue() {
        try { return (await client.post(`/staff/communications/queue/flush`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async retryQueueItem(id) {
        try { return (await client.post(`/staff/communications/queue/${id}/retry`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async recordAppointmentEvent(appointmentId, payload) {
        try { return (await client.post(`/staff/appointments/${appointmentId}/events`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async listAppointmentEvents(appointmentId) {
        try { return (await client.get(`/staff/appointments/${appointmentId}/events`)).data; }
        catch (e) { throw unwrap(e); }
    },

    // --- Sprint 29: Portal-owned payment-link email ---
    async sendPaymentLinkEmail(appointmentId, payload = {}) {
        try {
            const r = (await client.post(`/staff/appointments/${appointmentId}/payment/send-email`, payload)).data;
            cacheInvalidate(`staff:appointment:${appointmentId}`);
            cacheInvalidate("staff:timeline");
            return r;
        } catch (e) { throw unwrap(e); }
    },

    // --- Sprint 28: Migration checklist ---
    async migrationChecklist() {
        try { return (await client.get(`/staff/communications/migration/checklist`)).data; }
        catch (e) { throw unwrap(e); }
    },
    async migrationMarkDisabled(itemId, payload) {
        try { return (await client.post(`/staff/communications/migration/items/${itemId}/mark-disabled`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
    async migrationMarkTested(itemId, payload) {
        try { return (await client.post(`/staff/communications/migration/items/${itemId}/mark-tested`, payload)).data; }
        catch (e) { throw unwrap(e); }
    },
};
